import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Opening and closing by height, for things that appear and disappear while
 * someone is looking at them: a conflict row that answers itself when the ZIP
 * is added, a row the office dismisses, the whole acknowledged-issues note once
 * the last objection is gone, and the disclosure it all sits in.
 *
 * The collapse is a `1fr → 0fr` grid track rather than a max-height guess, so
 * it plays over the element's real height however far its text wraps, and only
 * the two properties that change are transitioned — animating `all` on a grid
 * container drags every inherited change into the same 260ms and is what makes
 * these panels look glitchy.
 */
export const COLLAPSE_MS = 260;

const COLLAPSE_TRANSITION =
  'transition-[grid-template-rows,opacity] duration-[260ms] ease-out motion-reduce:transition-none';

export function Collapse({
  open,
  id,
  className,
  children,
}: {
  open: boolean;
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      aria-hidden={!open || undefined}
      // Closed content keeps its DOM (so it can animate back open) but must not
      // be reachable by tab or read out.
      inert={!open || undefined}
      className={cn(
        'grid',
        COLLAPSE_TRANSITION,
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        className,
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/** The same, sideways — for a chip in a row of chips, which has to give up its
 *  width as it goes or it leaves a hole until the refetch lands. */
export function CollapseInline({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <span
      aria-hidden={!open || undefined}
      inert={!open || undefined}
      className={cn(
        'inline-grid transition-[grid-template-columns,opacity] duration-[260ms] ease-out motion-reduce:transition-none',
        open ? 'grid-cols-[1fr] opacity-100' : 'grid-cols-[0fr] opacity-0',
      )}
    >
      <span className="min-w-0 overflow-hidden">{children}</span>
    </span>
  );
}

/**
 * Keeps something mounted for one collapse after it stops being there, so it
 * can leave instead of vanishing between renders.
 *
 * `mounted` says whether to render it at all; `open` drives {@link Collapse}.
 * Something present on first render starts open — a card should not play its
 * whole contents in every time it is drawn.
 */
export function useCollapsePresence(present: boolean, ms = COLLAPSE_MS) {
  const [mounted, setMounted] = useState(present);
  const [open, setOpen] = useState(present);

  useEffect(() => {
    if (present) {
      setMounted(true);
      // One frame closed, so the browser has a height to animate FROM.
      const frame = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(frame);
    }
    setOpen(false);
    const timer = window.setTimeout(() => setMounted(false), ms);
    return () => window.clearTimeout(timer);
  }, [present, ms]);

  return { mounted, open };
}

/**
 * Rows on their way out, marked the moment they are acted on rather than when
 * the server confirms — the collapse has to start under the click, not a
 * round trip later.
 *
 * The mark is only meant to cover that gap: the refetch behind it removes the
 * row from the data for good. So each one expires. If the row is still in the
 * list well after the write should have landed, the write failed, and the row
 * has to come back rather than sit there invisible — which is what leaving
 * these marks to accumulate did, most visibly on a panel closed and reopened
 * after a fix, where a row that was never removed rendered at zero height.
 */
export function useDepartingRows(ms = COLLAPSE_MS * 4) {
  const [departing, setDeparting] = useState<ReadonlySet<string>>(() => new Set());
  const timers = useRef(new Map<string, number>());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const depart = useCallback(
    (key: string) => {
      setDeparting((previous) => new Set(previous).add(key));
      window.clearTimeout(timers.current.get(key));
      timers.current.set(
        key,
        window.setTimeout(() => {
          timers.current.delete(key);
          setDeparting((previous) => {
            if (!previous.has(key)) return previous;
            const next = new Set(previous);
            next.delete(key);
            return next;
          });
        }, ms),
      );
    },
    [ms],
  );

  return [departing, depart] as const;
}
